import React from 'react';
import { ArrowUpRight, ChevronRight, Mail, MapPin, Phone } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

const Footer = ({ navLinks, scrollToSection }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const serviceLinks = navLinks.find((link) => link.name === 'Services')?.subLinks || [];
  const careerLinks = navLinks.find((link) => link.name === 'Career')?.subLinks || [];
  const exploreLinks = navLinks.filter((link) => ['Home', 'About Us', 'Contact'].includes(link.name));

  const goTo = (link) => {
    if (link.isPage) {
      navigate(link.id);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (location.pathname !== '/') {
      navigate('/');
      window.setTimeout(() => scrollToSection(link.id, false), 80);
      return;
    }
    scrollToSection(link.id, false);
  };

  const renderLinks = (links) => (
    <ul className="mt-4 space-y-2.5">
      {links.map((link) => (
        <li key={link.id}>
          <button type="button" onClick={() => goTo(link)} className="group flex items-center gap-2 text-left text-sm text-slate-400 transition hover:text-white">
            <ChevronRight className="h-3.5 w-3.5 shrink-0 text-amber-400 opacity-0 transition group-hover:translate-x-0.5 group-hover:opacity-100" />
            <span className="-ml-5 transition group-hover:ml-0">{link.name}</span>
          </button>
        </li>
      ))}
    </ul>
  );

  return (
    <footer className="relative isolate overflow-hidden border-t border-white/10 bg-[#071426] text-white">
      <div className="pointer-events-none absolute -right-24 -top-40 h-96 w-96 rounded-full bg-blue-500/15 blur-[110px]" />
      <div className="pointer-events-none absolute -bottom-40 left-1/4 h-80 w-80 rounded-full bg-amber-400/10 blur-[110px]" />

      <div className="relative mx-auto max-w-7xl px-5 pb-7 pt-14 sm:px-8 sm:pt-16 lg:px-10">
        <div className="grid gap-10 border-b border-white/10 pb-12 sm:grid-cols-2 lg:grid-cols-[1.35fr_.7fr_1fr_1.15fr] lg:gap-8">
          <div>
            <div className="flex items-center gap-3">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white p-1.5 shadow-lg shadow-black/20">
                <img src="/logo.png" alt="GlobalEPRC logo" className="h-full w-full object-contain" />
              </span>
              <span>
                <span className="block text-base font-black tracking-wide">GlobalEPRC</span>
                <span className="mt-0.5 block text-[9px] font-bold uppercase tracking-[.16em] text-amber-300">Connect · Innovate · Grow</span>
              </span>
            </div>
            <p className="mt-5 max-w-sm text-sm leading-relaxed text-slate-400">A practical partner for technology, digital growth, ethical people solutions, and workforce support across India and international markets.</p>
            <button type="button" onClick={() => goTo({ id: '/contact', isPage: true })} className="mt-6 inline-flex items-center gap-2 rounded-lg bg-amber-400 px-4 py-2.5 text-xs font-extrabold text-slate-950 transition hover:bg-amber-300">
              Start a conversation <ArrowUpRight className="h-4 w-4" />
            </button>
          </div>

          <nav aria-label="Explore">
            <h2 className="text-[11px] font-black uppercase tracking-[.2em] text-white">Explore</h2>
            {renderLinks(exploreLinks)}
            <h3 className="mt-6 text-[10px] font-black uppercase tracking-[.18em] text-slate-500">Careers</h3>
            {renderLinks(careerLinks)}
          </nav>

          <nav aria-label="Services">
            <h2 className="text-[11px] font-black uppercase tracking-[.2em] text-white">Our Services</h2>
            {renderLinks(serviceLinks)}
          </nav>

          <address className="not-italic">
            <h2 className="text-[11px] font-black uppercase tracking-[.2em] text-white">Contact</h2>
            <ul className="mt-4 space-y-4">
              <li className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[.03] p-3.5">
                <Phone className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
                <span className="space-y-1 text-sm text-slate-300">
                  <a className="block transition hover:text-white" href="tel:+919606023500">+91 96060 23500</a>
                  <a className="block transition hover:text-white" href="tel:+919606023501">+91 96060 23501</a>
                  <a className="block text-emerald-400 font-bold transition hover:text-emerald-300 pt-1" href="https://wa.me/918610452001?text=Hi%20GlobalEPRC" target="_blank" rel="noopener noreferrer">
                    WhatsApp: +91 8610452001 <span className="text-[10px] bg-emerald-500/20 px-1.5 py-0.5 rounded text-emerald-300 font-normal">Text Us Here</span>
                  </a>
                </span>
              </li>
              <li className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[.03] p-3.5">
                <Mail className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
                <a className="break-all text-sm text-slate-300 transition hover:text-white" href="mailto:ethical.recruitment@globaleprc.com">ethical.recruitment@globaleprc.com</a>
              </li>
              <li className="flex items-start gap-3 px-1 text-sm leading-relaxed text-slate-400">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
                <span>Horamavu, Bengaluru,<br />Karnataka, India – 560113</span>
              </li>
            </ul>
          </address>
        </div>

        <div className="flex flex-col gap-2 pt-5 text-[11px] text-slate-500 sm:flex-row sm:items-center sm:justify-between">
          <p>© {new Date().getFullYear()} Global Ethical People Resourcing Consultancy. All rights reserved.</p>
          <p className="font-medium tracking-wide">Technology · People · Progress</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
